import { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Upload,
  Tag,
  Space,
  Input,
  Select,
  Row,
  Col,
  Card,
  Typography,
  Tooltip,
  message,
  Badge,
  Drawer,
} from "antd";
import {
  EditOutlined,
  FilePdfOutlined,
  UploadOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  InboxOutlined,
  CloseOutlined,
  ClearOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import config from "../../../../config";
import ActaEntregaPaatiPDFAlmacenar from "./FormatoActaEntregaPaatiPdf";
import { getAllActasEntregaParticipantes, postSoporteActaEntregaPaati, putFechaEntregaActaEntrega } from "../../../../services/paati/actasFormatos/paatiFormatoActaEntregas";
import Header166 from "../../../header/header";

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;
const baseUrl_server = config.baseUrl;

// ─── Estado → color y etiqueta ───────────────────────────────────────────────
const ESTADO_CONFIG = {
  SIN_FECHA_ENTREGA: { color: "warning", label: "Sin Fecha Entrega" },
  SIN_FIRMA: { color: "processing", label: "Sin Firma" },
  FIRMADO: { color: "success", label: "Firmado" },
  ENTREGADO: { color: "default", label: "Entregado" },
};

// ─── Sub-componente: Modal Fecha de Entrega ───────────────────────────────────
function ModalFechaEntrega({ open, record, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const fechaFormateada = values.fecha_entrega.format("YYYY-MM-DD");

      const res = await putFechaEntregaActaEntrega({
        id_salida: record.id_salida,
        fecha_entrega: fechaFormateada,
      });

      if (!res.success) {
        throw new Error(res.message || "Error al actualizar la fecha de entrega");
      }

      message.success(`Fecha de entrega registrada para ${record?.nombre_if}`);

      onSave({
        ...record,
        fecha_entrega: fechaFormateada,
        estado: "SIN_FIRMA",
      });

      form.resetFields();
      onClose();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "No se pudo actualizar la fecha de entrega");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined style={{ color: "#fa8c16" }} />
          <span>Registrar Fecha de Entrega</span>
        </Space>
      }
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleOk}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={loading}
      width={480}
    >
      {record && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Participante:</Text>
          <br />
          <Text strong>{record.nombre_if}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Código SIR: {record.codigo_sir} · {record.codigo_pre}
          </Text>
        </div>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="fecha_entrega"
          label="Fecha de Entrega"
          rules={[{ required: true, message: "Por favor seleccione una fecha" }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="Seleccionar fecha"
            disabledDate={(d) => d && d < dayjs().startOf("day")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Sub-componente: Modal Cargar PDF ─────────────────────────────────────────
function ModalCargarPDF({ open, record, onClose, onSave }) {
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      if (fileList.length === 0) {
        message.warning("Por favor seleccione un archivo PDF");
        return;
      }

      setLoading(true);

      const fecha = new Date(record.fecha_entrega);
      const dia = fecha.getUTCDate();
      const anio = fecha.getUTCFullYear();
      const mesNumero = String(fecha.getUTCMonth() + 1).padStart(2, "0");
      const mesTexto = fecha.toLocaleDateString("es-CO", { month: "long", timeZone: "UTC" });
      const mesTextoCapitalizado = mesTexto.charAt(0).toUpperCase() + mesTexto.slice(1);

      const departamento = record.departamento;
      const file = fileList[0]?.originFileObj;

      if (!file) { message.error("Archivo inválido"); return; }

      const nombreArchivo = `1_Acta_Entrega_${anio}_${mesNumero}_${dia}.pdf`;
      const formDataToSend = new FormData();
      formDataToSend.append("ruta", `${mesTextoCapitalizado}_${anio}/${departamento}/${record.codigo_pre_dir}/`);
      formDataToSend.append("nombre_archivo", nombreArchivo);
      formDataToSend.append("file", file, nombreArchivo);

      const response = await postSoporteActaEntregaPaati(formDataToSend, record.id_salida);

      if (response?.success) {
        message.success("PDF subido correctamente");
        const rutaPDF = `${mesTextoCapitalizado}_${anio}/${departamento}/${record.codigo_pre_dir}/${nombreArchivo}`;
        setFileList([]);
        onClose();
        onSave({ ...record, estado: "FIRMADO", baseUrl: `/documentosFAO/PAATI/Acta_entregas/${rutaPDF}` });
      } else {
        message.error(response?.message || "Error al subir el archivo");
      }
    } catch (err) {
      console.error("❌ Error al enviar el PDF:", err);
      message.error("Error al subir el archivo");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    accept: ".pdf",
    maxCount: 1,
    fileList,
    beforeUpload: (file) => {
      const isPDF = file.type === "application/pdf";
      if (!isPDF) message.error("Solo se permiten archivos PDF");
      return isPDF ? false : Upload.LIST_IGNORE;
    },
    onChange: ({ fileList: fl }) => setFileList(fl),
  };

  return (
    <Modal
      title={
        <Space>
          <UploadOutlined style={{ color: "#52c41a" }} />
          <span>Cargar Documento PDF</span>
        </Space>
      }
      open={open}
      onCancel={() => { setFileList([]); onClose(); }}
      onOk={handleOk}
      okText="Cargar"
      cancelText="Cancelar"
      confirmLoading={loading}
      width={500}
    >
      {record && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Participante:</Text>
          <br />
          <Text strong>{record.nombre_if}</Text>
        </div>
      )}
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Haz clic o arrastra el PDF aquí</p>
        <p className="ant-upload-hint">Solo archivos PDF · Máx. 1 archivo</p>
      </Dragger>
    </Modal>
  );
}

// ─── Cabecera de Filtros ──────────────────────────────────────────────────────
function FiltrosCabecera({ data, filtros, onFiltroChange, onActualizar, onLimpiar, loading }) {
  // Opciones únicas derivadas de los datos cargados
  const departamentos = [...new Set(data.map((d) => d.departamento).filter(Boolean))].sort();
  const municipios = [...new Set(
    data
      .filter((d) => !filtros.departamento || d.departamento === filtros.departamento)
      .map((d) => d.municipio)
      .filter(Boolean)
  )].sort();

  const hayFiltrosActivos =
    filtros.codigo_sir || filtros.departamento || filtros.municipio || filtros.estado;

  return (
    <Card
      style={{
        margin: "16px 20px 0",
        borderRadius: "12px 12px 0 0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderBottom: "2px solid #e6f4ff",
        background: "linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)",
      }}
      bodyStyle={{ padding: "16px 20px" }}
    >
      {/* Título sección filtros */}
      <Row align="middle" style={{ marginBottom: 14 }}>
        <Col>
          <Space size={8}>
            <FilterOutlined style={{ color: "#1677ff", fontSize: 15 }} />
            <Text strong style={{ fontSize: 14, color: "#1677ff" }}>
              Filtros de búsqueda
            </Text>
            {hayFiltrosActivos && (
              <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                Filtros activos
              </Tag>
            )}
          </Space>
        </Col>
      </Row>

      {/* Controles */}
      <Row gutter={[12, 12]} align="bottom">
        {/* Código SIR */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Código SIR
            </Text>
            <Input
              placeholder="Buscar código SIR..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filtros.codigo_sir}
              onChange={(e) => onFiltroChange("codigo_sir", e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Space>
        </Col>

        {/* Departamento */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Departamento
            </Text>
            <Select
              placeholder="Seleccionar departamento"
              value={filtros.departamento || undefined}
              onChange={(val) => {
                onFiltroChange("departamento", val ?? "");
                onFiltroChange("municipio", "");
              }}
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
              showSearch
              optionFilterProp="children"
            >
              {departamentos.map((d) => (
                <Option key={d} value={d}>{d}</Option>
              ))}
            </Select>
          </Space>
        </Col>

        {/* Municipio */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Municipio
            </Text>
            <Select
              placeholder="Seleccionar municipio"
              value={filtros.municipio || undefined}
              onChange={(val) => onFiltroChange("municipio", val ?? "")}
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
              showSearch
              optionFilterProp="children"
              disabled={!filtros.departamento}
            >
              {municipios.map((m) => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>
          </Space>
        </Col>

        {/* Estado */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Estado
            </Text>
            <Select
              placeholder="Seleccionar estado"
              value={filtros.estado || undefined}
              onChange={(val) => onFiltroChange("estado", val ?? "")}
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
            >
              {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
                <Option key={key} value={key}>
                  <Badge status={cfg.color} text={cfg.label} />
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>

      {/* Botones */}
      <Row justify="end" style={{ marginTop: 14 }} gutter={8}>
        <Col>
          <Tooltip title="Limpiar todos los filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={onLimpiar}
              disabled={!hayFiltrosActivos}
              style={{
                borderRadius: 8,
                borderColor: hayFiltrosActivos ? "#ff4d4f" : undefined,
                color: hayFiltrosActivos ? "#ff4d4f" : undefined,
              }}
            >
              Limpiar
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Recargar datos desde el servidor">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={onActualizar}
              style={{ borderRadius: 8 }}
            >
              Actualizar
            </Button>
          </Tooltip>
        </Col>
      </Row>
    </Card>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function EntregasTable() {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataPDF, setDataPDF] = useState([]);
  const [drawerPdfVisible, setDrawerPdfVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Filtros
  const FILTROS_VACIOS = { codigo_sir: "", estado: "", departamento: "", municipio: "" };
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);

  // Modales
  const [modalFecha, setModalFecha] = useState({ open: false, record: null });
  const [modalPDF, setModalPDF] = useState({ open: false, record: null });

  // ── Carga de datos ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllActasEntregaParticipantes();
      if (res.success) {
        setData(res.data);
        setFiltered(res.data);
      } else {
        message.error("Error al cargar los datos");
      }
    } catch (e) {
      message.error("No se pudo conectar con el servidor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtrado reactivo ───────────────────────────────────────────────────────
  useEffect(() => {
    let result = [...data];
    if (filtros.codigo_sir)
      result = result.filter((r) =>
        r.codigo_sir?.toLowerCase().includes(filtros.codigo_sir.toLowerCase())
      );
    if (filtros.estado) result = result.filter((r) => r.estado === filtros.estado);
    if (filtros.departamento) result = result.filter((r) => r.departamento === filtros.departamento);
    if (filtros.municipio) result = result.filter((r) => r.municipio === filtros.municipio);
    setFiltered(result);
  }, [filtros, data]);

  const handleFiltro = (key, val) => setFiltros((prev) => ({ ...prev, [key]: val ?? "" }));
  const limpiarFiltros = () => setFiltros(FILTROS_VACIOS);

  // ── Actualizar fila localmente tras modal ───────────────────────────────────
  const actualizarFila = (updated) => {
    setData((prev) => prev.map((r) => (r.participante_id === updated.participante_id ? updated : r)));
  };

  // ── Ver PDF ─────────────────────────────────────────────────────────────────
  const handleVerPDF = (record) => {
    const fecha = new Date(record.fecha_entrega);
    const dia = fecha.getUTCDate();
    const mes = fecha.toLocaleDateString("es-CO", { month: "long" });
    const anio = fecha.getUTCFullYear();

    setDataPDF({
      nombre_representante: record.nombre_if,
      codigo_pre: record.codigo_pre,
      proyecto_productivo: record.descripcion_proyecto,
      direccion_proyecto_productivo: record.direccion_proyecto_productivo,
      linea_productiva: record.actividad_productiva,
      grupo_territorial: record.grupo_territorial,
      departamento: record.departamento,
      municipio: record.municipio,
      lista_participantes: record.lista_participantes,
      lista_insumos: record.lista_insumos,
      valorTotalEntrega: record.valor_total,
      sitio_entrega: record.sitio_entrega,
      codigo_interno: record.codActa,
      seriales: "",
      valor_total: record.valor_total,
      dia,
      mes,
      vigencia: anio,
      personas_reincorporacion: record.lista_participantes.length,
      tipo_forma: record.tipo_forma,
    });
    setSelectedRecord(record);
    setDrawerPdfVisible(true);
  };

  const handleCloseDrawers = () => {
    setDrawerPdfVisible(false);
    setSelectedRecord(null);
    setDataPDF([]);
  };

  // ── Columnas ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Código SIR",
      dataIndex: "codigo_sir",
      key: "codigo_sir",
      width: 120,
      fixed: "left",
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: "Participante",
      dataIndex: "nombre_if",
      key: "nombre_if",
      width: 220,
      ellipsis: true,
      render: (v, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>CC: {r.identificacion}</Text>
        </div>
      ),
    },
    { title: "Código PRE", dataIndex: "codigo_pre", key: "codigo_pre", width: 130 },
    {
      title: "Proyecto",
      dataIndex: "nombre_proyecto",
      key: "nombre_proyecto",
      width: 150,
      ellipsis: true,
      render: (v, r) => (
        <div>
          <Text>{v}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.actividad_productiva}</Text>
        </div>
      ),
    },
    {
      title: "Ubicación",
      key: "ubicacion",
      width: 100,
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 12 }}>{r.departamento}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.municipio}</Text>
        </div>
      ),
    },
    {
      title: "Sexo",
      dataIndex: "sexo_sir",
      key: "sexo_sir",
      width: 80,
      render: (v) => (
        <Tag color={v === "Mujer" ? "magenta" : "blue"} style={{ fontSize: 11 }}>{v}</Tag>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 160,
      render: (estado) => {
        const cfg = ESTADO_CONFIG[estado] || { color: "default", label: estado };
        return <Badge status={cfg.color} text={<Text style={{ fontSize: 12 }}>{cfg.label}</Text>} />;
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space size={4} wrap>
          <Tooltip title="Registrar fecha de entrega">
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              disabled={record.estado === "FIRMADO"}
              onClick={() => setModalFecha({ open: true, record })}
              style={{
                background: record.estado === "SIN_FECHA_ENTREGA" ? "#fa8c16" : undefined,
                borderColor: record.estado === "SIN_FECHA_ENTREGA" ? "#fa8c16" : undefined,
              }}
            >
              Fecha
            </Button>
          </Tooltip>

          <Tooltip title="Descargar PDF para firma">
            <Button
              type="default"
              icon={<FilePdfOutlined />}
              size="small"
              shape="circle"
              disabled={record.estado !== "SIN_FIRMA"}
              onClick={() => {
                if (record.baseUrl) {
                  window.open(`${baseUrl_server}/${record.baseUrl}?ts=${Date.now()}`, "_blank");
                } else {
                  handleVerPDF(record);
                }
              }}
            />
          </Tooltip>

          <Tooltip title={record.estado === "FIRMADO" ? "Visualizar documento" : "Cargar documento firmado"}>
            <Button
              size="small"
              icon={record.estado === "FIRMADO" ? <EyeOutlined /> : <UploadOutlined />}
              disabled={record.estado === "SIN_FECHA_ENTREGA"}
              onClick={() => {
                if (record.estado === "FIRMADO") {
                  if (record.baseUrl) {
                    const url = record.baseUrl.startsWith("http")
                      ? record.baseUrl
                      : `${baseUrl_server}${record.baseUrl}`;
                    window.open(url, "_blank");
                  } else {
                    message.warning("No hay documento para visualizar");
                  }
                } else {
                  setModalPDF({ open: true, record });
                }
              }}
              style={{
                color: record.estado === "FIRMADO" ? "#52c41a" : undefined,
                borderColor: record.estado === "FIRMADO" ? "#52c41a" : undefined,
              }}
            >
              {record.estado === "FIRMADO" ? "Ver PDF" : "Cargar"}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <Header166 type="salidabodega_paati" />

      {/* Cabecera de filtros */}
      <FiltrosCabecera
        data={data}
        filtros={filtros}
        onFiltroChange={handleFiltro}
        onActualizar={fetchData}
        onLimpiar={limpiarFiltros}
        loading={loading}
      />

      {/* Tabla */}
      <Card
        style={{
          margin: "0 20px 20px",
          borderRadius: "0 0 12px 12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        bodyStyle={{ paddingTop: 0 }}
      >
        {/* Resumen de resultados */}
        <Row
          align="middle"
          style={{
            padding: "12px 0 8px",
            borderBottom: "1px solid #f0f0f0",
            marginBottom: 8,
          }}
        >
          <Col>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Mostrando{" "}
              <Text strong style={{ color: "#1677ff" }}>{filtered.length}</Text>{" "}
              de{" "}
              <Text strong>{data.length}</Text>{" "}
              registros
            </Text>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="participante_id"
          loading={loading}
          size="small"
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} de ${total} registros`,
            size: "small",
          }}
          rowClassName={(r) => r.estado === "SIN_FECHA_ENTREGA" ? "row-warning" : ""}
        />
      </Card>

      {/* Modal: Fecha de Entrega */}
      <ModalFechaEntrega
        open={modalFecha.open}
        record={modalFecha.record}
        onClose={() => setModalFecha({ open: false, record: null })}
        onSave={(updated) => actualizarFila({ ...updated, estado: "SIN_FIRMA" })}
      />

      {/* Modal: Cargar PDF */}
      <ModalCargarPDF
        open={modalPDF.open}
        record={modalPDF.record}
        onClose={() => setModalPDF({ open: false, record: null })}
        onSave={(updated) => actualizarFila({ ...updated, estado: "FIRMADO" })}
      />

      {/* Drawer PDF */}
      <Drawer
        title={`PDF - Acta: ${selectedRecord?.codActa || ""}`}
        placement="right"
        onClose={handleCloseDrawers}
        open={drawerPdfVisible}
        width="100%"
        style={{ maxWidth: "100vw" }}
        destroyOnClose
        closeIcon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
      >
        <ActaEntregaPaatiPDFAlmacenar {...dataPDF} />
      </Drawer>

      <style>{`
        .row-warning { background-color: #fffbe6 !important; }
        .row-warning:hover > td { background-color: #fff3cc !important; }
      `}</style>
    </div>
  );
}